import React from 'react';

export default function Skills() {
  const skillCategories = [
    {
      title: 'Cloud & Infrastructure',
      icon: '☁️',
      skills: ['AWS', 'GCP', 'DevOps', 'IaC (Terraform)', 'Containerization', 'MLOps', 'CI/CD'],
    },
    {
      title: 'Programming',
      icon: '💻',
      skills: ['Python', 'TypeScript', 'JavaScript', 'SQL', 'NoSQL', 'React', 'Node.js'],
    },
    {
      title: 'Data & AI',
      icon: '🤖',
      skills: ['Machine Learning', 'Deep Learning', 'TensorFlow', 'Data Analysis', 'Tableau', 'Excel'],
    },
    {
      title: 'Product & Business',
      icon: '📊',
      skills: ['Product Management', 'Agile', 'Stakeholder Management', 'Strategy', 'Consulting'],
    },
  ];

  const languages = [
    { name: 'Spanish', level: 'Native', percentage: 100 },
    { name: 'English', level: 'Fluent', percentage: 95 },
    { name: 'German', level: 'Advanced', percentage: 75 },
    { name: 'French', level: 'Intermediate', percentage: 60 },
  ];

  return (
    <section id="skills" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Skills & Expertise
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto">
            Technical skills and competencies developed through education and professional experience
          </p>
        </div>

        {/* Technical Skills */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-16">
          {skillCategories.map((category, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition"
            >
              <div className="text-4xl sm:text-5xl mb-3 sm:mb-4">{category.icon}</div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
                {category.title}
              </h3>
              <div className="flex flex-wrap gap-2">
                {category.skills.map((skill, i) => (
                  <span
                    key={i}
                    className="px-2.5 py-1 sm:px-3 bg-blue-50 text-blue-700 rounded-lg text-xs sm:text-sm font-medium"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Languages */}
        <div className="bg-white rounded-xl p-6 sm:p-8 shadow-lg">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">
            Languages
          </h3>
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
            {languages.map((lang, index) => (
              <div key={index}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm sm:text-base font-semibold text-gray-900">{lang.name}</span>
                  <span className="text-xs sm:text-sm text-gray-600">{lang.level}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 sm:h-3">
                  <div
                    className="bg-blue-600 h-2.5 sm:h-3 rounded-full transition-all duration-1000"
                    style={{ width: `${lang.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Certifications & Achievements */}
        <div className="mt-12 sm:mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-5 sm:p-6 text-center">
            <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">🏆</div>
            <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1 sm:mb-2">AWS Champion</div>
            <div className="text-sm sm:text-base text-blue-100">Worldwide Problem Solving 2023</div>
          </div>
          <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white rounded-xl p-5 sm:p-6 text-center">
            <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">📝</div>
            <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1 sm:mb-2">4 AWS Blog Posts</div>
            <div className="text-sm sm:text-base text-indigo-100">Published Technical Articles</div>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-5 sm:p-6 text-center sm:col-span-2 lg:col-span-1">
            <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">🎓</div>
            <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1 sm:mb-2">Top 3%</div>
            <div className="text-sm sm:text-base text-purple-100">Graduating Class Rank</div>
          </div>
          <a
            href="https://www.credly.com/users/alberto-menendez.ac26ae8f"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-xl p-5 sm:p-6 text-center hover:shadow-xl hover:from-emerald-600 hover:to-emerald-700 transition block"
          >
            <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">📜</div>
            <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1 sm:mb-2">6 AWS Certifications</div>
            <div className="text-sm sm:text-base text-emerald-100">View on Credly</div>
          </a>
        </div>
      </div>
    </section>
  );
}
